import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Upload, Download } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/services/api';
import { formatDate, getProtectionLevelLabel } from '@/lib/utils';
import { ModuleView } from '@/components/prisoners/ModuleView';
import { NeedleView } from '@/components/prisoners/NeedleView';
import { TimelineView } from '@/components/prisoners/TimelineView';
import { DocumentsView } from '@/components/prisoners/DocumentsView';
import { DocumentUploadDialog } from '@/components/prisoners/DocumentUploadDialog';
import { MasterDataView } from '@/components/prisoners/MasterDataView';
import { useAuth } from '@/contexts/AuthContext';
import type { Prisoner } from '@/types';

type ViewMode = 'timeline' | 'module' | 'needle' | 'documents' | 'stammdaten';

export function PrisonerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view') as ViewMode | null;
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const { hasPermission } = useAuth();

  const availableViews: Array<{ key: ViewMode; label: string; permission: string }> = [
    { key: 'timeline', label: 'Timeline', permission: 'view.prisoner.timeline' },
    { key: 'module', label: 'Modulansicht', permission: 'view.prisoner.modules' },
    { key: 'needle', label: 'GVO-Nadelansicht', permission: 'view.prisoner.needle' },
    { key: 'documents', label: 'Dokumente', permission: 'view.prisoner.documents' },
    { key: 'stammdaten', label: 'Stammdaten', permission: 'view.prisoner.stammdaten' },
  ];
  const allowedViews = availableViews.filter((view) => hasPermission(view.permission));
  const activeView = allowedViews.find((view) => view.key === viewMode) ? viewMode : allowedViews[0]?.key;

  useEffect(() => {
    if (viewParam && allowedViews.some((view) => view.key === viewParam)) {
      setViewMode(viewParam);
    }
  }, [viewParam, allowedViews]);

  const handleViewChange = (nextView: ViewMode) => {
    setViewMode(nextView);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', nextView);
    setSearchParams(nextParams, { replace: true });
  };

  const { data: prisoner, isLoading, error } = useQuery({
    queryKey: ['prisoner', id],
    queryFn: () => api.getPrisoner(id!),
    enabled: !!id,
  });

  const { data: criminalCases } = useQuery({
    queryKey: ['cases', id],
    queryFn: () => api.getCriminalCases(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Lädt Gefangenendaten...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-2">
        <p className="text-red-500">Fehler beim Laden: {(error as Error).message}</p>
        <p className="text-sm text-muted-foreground">ID: {id}</p>
      </div>
    );
  }

  if (!prisoner) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Gefangener nicht gefunden.</p>
      </div>
    );
  }

  if (allowedViews.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Für diese Gefangenenansicht fehlen Berechtigungen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {prisoner.lastName}, {prisoner.firstName}
          </h1>
          <p className="font-mono text-muted-foreground">
            {prisoner.prisonerNumber}
          </p>
        </div>
        <div className="flex gap-2">
          {hasPermission('documents.upload') && (
            <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Dokument hinzufügen
            </Button>
          )}
          {hasPermission('menu.documents.view') && (
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Exportieren
            </Button>
          )}
        </div>
      </div>

      {/* Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">Geburtsdatum</p>
              <p className="font-medium">{formatDate(prisoner.dateOfBirth)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Aufnahmedatum</p>
              <p className="font-medium">{formatDate(prisoner.admissionDate)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <p className="font-medium">{prisoner.status}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Schutzstufe</p>
              <p className="font-medium">
                {getProtectionLevelLabel(prisoner.protectionLevel)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Toggle */}
      <div className="flex items-center gap-4 border-b pb-4">
        <span className="text-sm font-medium text-muted-foreground">Ansicht:</span>
        <div className="flex rounded-lg border p-1">
          {allowedViews.map((view) => (
            <Button
              key={view.key}
              variant={activeView === view.key ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleViewChange(view.key)}
            >
              {view.label}
            </Button>
          ))}
        </div>
      </div>

      {/* View Content */}
      {activeView === 'timeline' && <TimelineView prisonerId={id!} />}
      {activeView === 'module' && <ModuleView prisonerId={id!} />}
      {activeView === 'needle' && <NeedleView prisonerId={id!} />}
      {activeView === 'documents' && (
        <DocumentsView
          prisonerId={id!}
          highlightDocumentId={searchParams.get('documentId') || undefined}
          highlightRecordItemId={searchParams.get('recordItemId') || undefined}
        />
      )}
      {activeView === 'stammdaten' && <MasterDataView prisonerId={id!} />}

      {/* Upload Dialog */}
      <DocumentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        prisonerId={id!}
        criminalCases={criminalCases}
      />
    </div>
  );
}
